/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button, Text } from '@/shared/ui';
import { cn, tailwindUtils } from '@/shared/ui/utils/tailwind';
import { TrialQuotaNotice, useSeatUsage } from '@features/subscription';
import { trialNoticeLevel } from '@/shared/utils/trial-quota';
import { buildLimitView } from '@/shared/utils/partnerships-limit';
import { useAgencyId } from '../hooks/useAgencyId';

/**
 * The monthly-cycle partnership-search limit, shown on the Partnerships home.
 *
 * It surfaces the current cycle's usage (searches left / used of total) and
 * puts the upsell one tap away: the CTA routes straight to the subscription
 * settings, where the EXISTING upsell engine (`decideOffer` + addon pack /
 * plan upgrade) lives — this banner does not reimplement any upsell flow, it
 * only makes the raise-the-limit action very easy to reach from where the
 * agent hits the wall. The whole block self-hides when usage cannot be read
 * (never draws a bar against an unknown denominator) and when the plan is
 * unmetered.
 *
 * @returns The limit + upsell banner, or null when there is nothing to show.
 */
export function SearchLimitBanner() {
  const { t } = useTranslation('features/partnerships');
  const navigate = useNavigate();
  const agencyId = useAgencyId();
  const { data, isLoading } = useSeatUsage(agencyId);

  const view = buildLimitView(data?.partnership_search ?? null);
  // Nothing to say while loading, when usage is unreadable, or when the plan
  // does not meter searches: a limit banner for an unmetered plan is noise.
  if (isLoading || !view || view.unlimited) return null;

  // RN-07: in the free period the last of the 3 searches ends in the trial
  // block (with the way out), not in the plan-cycle banner.
  if (trialNoticeLevel(data, 'partnershipSearch') === 'block') {
    return <TrialQuotaNotice agencyId={agencyId} kind="partnershipSearch" />;
  }

  const goToUpsell = () => navigate('../settings?section=plano');
  const hint = view.exhausted ? t('limit.exhausted_hint') : t('limit.upsell_hint');

  return (
    <div
      data-testid="partnerships-limit-banner"
      className={cn(
        tailwindUtils.card,
        'flex flex-wrap items-center justify-between gap-[var(--space-3)] p-[var(--space-4)]',
      )}
    >
      <div className="flex flex-col gap-[var(--space-1)]">
        <Text variant="body" size="sm" fw={600}>{t('limit.title')}</Text>
        <div className="flex items-baseline gap-[var(--space-2)]">
          <Text span size="lg" fw={700} className="text-[var(--seed-base)]" data-testid="limit-remaining">
            {t('limit.remaining', { count: view.remaining })}
          </Text>
          <Text span size="xs" className="text-[var(--text-tertiary)]">
            {t('limit.used_of', { used: view.used, total: view.total })}
          </Text>
        </div>
        <Text span size="xs" className="text-[var(--text-secondary)]">{hint}</Text>
      </div>
      <Button
        variant="solid"
        size="md"
        onClick={goToUpsell}
        data-testid="partnerships-upsell-cta"
      >
        {t('limit.upsell_cta')}
      </Button>
    </div>
  );
}
